// /planner/video-qc/:campaignId — sidebar page. Pick a campaign (the same
// list as the Campaign page), then check its posted creator videos against
// the brief. Styled with the Discover V2 theme.
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import DiscoverThemeProvider from '../InfluencerDiscoverV2/DiscoverThemeProvider';
import '../InfluencerDiscoverV2/discoverV2.css';
import './videoQc.css';
import CampaignPicker from './CampaignPicker';
import CampaignQc, { QcSkeleton } from './CampaignQc';
import QcHero from './QcHero';
import { listCampaignVideos, listCampaigns } from './campaignSource';
import { errorText } from './qcClient';
import { AiMark } from './aiUi';
import { C, Empty, SURFACE } from './ui';

export const PAGE_PATH = '/planner/video-qc';

function useCampaigns() {
    const [state, setState] = useState({ loading: true, error: '', items: [] });
    useEffect(() => {
        let active = true;
        listCampaigns()
            .then((items) => active && setState({ loading: false, error: '', items }))
            .catch((err) => active && setState({ loading: false, error: errorText(err), items: [] }));
        return () => { active = false; };
    }, []);
    return state;
}

// The selected campaign's posted videos (YouTube + Instagram feeds).
// `ready` is only true once data for THIS campaignId has arrived, so the QC
// workspace never mounts with an empty or previous campaign's video list.
function useCampaignVideos(campaignId) {
    const [state, setState] = useState({ forId: null, error: '', videos: [], partial: false });
    useEffect(() => {
        if (!campaignId) return undefined;
        let active = true;
        listCampaignVideos(campaignId)
            .then(({ videos, partial }) => active && setState({ forId: campaignId, error: '', videos, partial }))
            .catch((err) => active && setState({ forId: campaignId, error: errorText(err), videos: [], partial: false }));
        return () => { active = false; };
    }, [campaignId]);
    const ready = Boolean(campaignId) && state.forId === campaignId;
    return { ...state, ready, loading: Boolean(campaignId) && !ready };
}

export default function VideoQcPage() {
    const { campaignId } = useParams();
    const history = useHistory();
    const campaigns = useCampaigns();
    const current = useCampaignVideos(campaignId);
    const [stats, setStats] = useState(null);
    const onStats = useCallback((next) => setStats(next), []);

    // No campaign in the URL: open the most recent one so the page is never blank.
    useEffect(() => {
        if (!campaignId && campaigns.items.length) history.replace(`${PAGE_PATH}/${campaigns.items[0].id}`);
    }, [campaignId, campaigns.items, history]);

    useEffect(() => { setStats(null); }, [campaignId]);

    const selected = campaigns.items.find((c) => String(c.id) === String(campaignId)) || null;

    let body;
    if (campaigns.error && !campaignId) {
        body = <div role="alert" style={{ ...SURFACE, padding: 20, color: '#991b1b', fontSize: 13 }}>Couldn't load campaigns: {campaigns.error}</div>;
    } else if (!campaignId) {
        body = campaigns.loading ? <QcSkeleton label="Loading campaigns…" /> : (
            <div style={{ ...SURFACE, padding: 20 }}>
                <Empty icon={<AiMark size={48} />} title="No campaigns yet">Create a campaign first, then check its videos here.</Empty>
            </div>
        );
    } else if (current.loading) {
        body = <QcSkeleton label="Loading campaign videos…" />;
    } else if (current.error) {
        body = <div role="alert" style={{ ...SURFACE, padding: 20, color: '#991b1b', fontSize: 13 }}>Couldn't load this campaign's videos: {current.error}</div>;
    } else {
        body = (
            <>
                {current.partial && (
                    <div role="status" style={{ background: C.goldSoft, border: '1px solid #fde68a', color: '#92400e', borderRadius: 12, padding: '10px 14px', fontSize: 13 }}>
                        Some of this campaign's posts couldn't be loaded (one platform's feed failed). Reload to try again.
                    </div>
                )}
                <CampaignQc key={campaignId} campaignId={campaignId} videos={current.videos} onStats={onStats}
                    campaignBrief={selected?.objective || ''} />
            </>
        );
    }

    return (
        <DiscoverThemeProvider>
            <Box className="discover-v2-root vqc-root" sx={{ minHeight: 'calc(100vh - 120px)', textAlign: 'left' }}>
                <QcHero stats={stats} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <CampaignPicker campaigns={campaigns.items} loading={campaigns.loading} selected={selected}
                        onSelect={(c) => history.push(`${PAGE_PATH}/${c.id}`)} />
                    {body}
                </Box>
            </Box>
        </DiscoverThemeProvider>
    );
}
